'use client';

import { useMemo } from 'react';

const dayFirstCache = new Map<string, boolean>();

/**
 * Detects whether a locale formats calendar dates with day before month.
 * Results are cached per locale string to avoid repeated Intl allocations.
 *
 * @param locale - BCP-47 locale string (for example: en-GB, fr-FR, en-US)
 * @returns True when day appears before month, false otherwise
 */
const isDayFirstLocale = (locale?: string): boolean => {
  if (!locale) return false;
  if (dayFirstCache.has(locale)) {
    return !!dayFirstCache.get(locale);
  }

  const parts = new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).formatToParts(new Date(2000, 10, 22));

  const dayIndex = parts.findIndex((part) => part.type === 'day');
  const monthIndex = parts.findIndex((part) => part.type === 'month');
  const result = dayIndex !== -1 && monthIndex !== -1 && dayIndex < monthIndex;

  dayFirstCache.set(locale, result);
  return result;
};

/**
 * ### useGetDateValueFormat
 *
 * @description
 * Returns a dayjs value format string ('DD/MM/YYYY' or 'MM/DD/YYYY') based on the
 * user's browser locale. Day-first locales (e.g. en-GB) get 'DD/MM/YYYY';
 * month-first (e.g. en-US) get 'MM/DD/YYYY'. Locale is resolved internally.
 *
 * @example
 * const valueFormat = useGetDateValueFormat();
 * dayjs(date).format(valueFormat);
 *
 * @returns Dayjs format string for date values
 */
const useGetDateValueFormat = (): string => {
  const locale = typeof navigator !== 'undefined' ? navigator.language : undefined;

  return useMemo(() => (isDayFirstLocale(locale) ? 'DD/MM/YYYY' : 'MM/DD/YYYY'), [locale]);
};

export default useGetDateValueFormat;
